import { useState } from 'react';

import { CopyButton } from '@/components/copy-button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { CallDetailProvider, useCallDetail } from '@/context/CallProvider';

import { useCallDetailQuery } from '@/query/call.query';

import { CallDetails } from './components/call-details';
import { CallExtractedData } from './components/call-extracted-data';
import { CallRecording } from './components/call-recording';
import { CallSentiment } from './components/call-sentiment';
import { CallSummary } from './components/call-summary';
import { CallTranscript } from './components/call-transcript';

type Props = {
  toggle: () => void;
  callId: number;
};

const Content = () => {
  const { call: callDetail } = useCallDetail();

  const [activeDetailTab, setActiveDetailTab] = useState<'details' | 'transcript'>('details');

  const hasExtractedData = Object.entries(callDetail?.extracted_data).length > 0;
  const hasSummary = !!callDetail?.summary;
  const hasSentiment = !!callDetail?.sentiment;

  return (
    <>
      <SheetHeader>
        <SheetTitle>Call details</SheetTitle>
        <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
          <div className="text-sm">{callDetail.callId}</div>
          <CopyButton value={callDetail.callId} message="Call ID copied to clipboard" />
        </div>
      </SheetHeader>
      {callDetail && (
        <div className="flex-1 min-h-0 flex flex-col space-y-4 px-4">
          <div className="flex-1 min-h-0 flex flex-col">
            <CallRecording />

            <Tabs
              defaultValue={activeDetailTab}
              className="w-full gap-4 h-full min-h-0 flex flex-col flex-1"
              onValueChange={(v: string) => setActiveDetailTab(v as 'details' | 'transcript')}
            >
              <TabsList className="text-foreground h-auto gap-2 rounded-none border-b bg-transparent px-0 py-1 w-full justify-start">
                <TabsTrigger
                  value="details"
                  className="hover:bg-primary/10 hover:text-primary data-[state=active]:after:bg-primary data-[state=active]:hover:bg-primary/10 relative after:absolute after:inset-x-0 after:bottom-0 after:-mb-1.5 after:h-0.5 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  Details
                </TabsTrigger>
                <TabsTrigger
                  value="transcript"
                  className="hover:bg-primary/10 hover:text-primary data-[state=active]:after:bg-primary data-[state=active]:hover:bg-primary/10 relative after:absolute after:inset-x-0 after:bottom-0 after:-mb-1.5 after:h-0.5 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  Transcript
                </TabsTrigger>
                {hasSummary && (
                  <TabsTrigger
                    value="summary"
                    className="hover:bg-primary/10 hover:text-primary data-[state=active]:after:bg-primary data-[state=active]:hover:bg-primary/10 relative after:absolute after:inset-x-0 after:bottom-0 after:-mb-1.5 after:h-0.5 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    Summary
                  </TabsTrigger>
                )}

                {hasExtractedData && (
                  <TabsTrigger
                    value="extracted_data"
                    className="hover:bg-primary/10 hover:text-primary data-[state=active]:after:bg-primary data-[state=active]:hover:bg-primary/10 relative after:absolute after:inset-x-0 after:bottom-0 after:-mb-1.5 after:h-0.5 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    Extracted data
                  </TabsTrigger>
                )}

                {hasSentiment && (
                  <TabsTrigger
                    value="sentiment"
                    className="hover:bg-primary/10 hover:text-primary data-[state=active]:after:bg-primary data-[state=active]:hover:bg-primary/10 relative after:absolute after:inset-x-0 after:bottom-0 after:-mb-1.5 after:h-0.5 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    Sentiment
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="details" className="flex-1 h-full min-h-0 flex flex-col">
                <CallDetails />
              </TabsContent>

              <TabsContent value="transcript" className="flex-1 h-full min-h-0 flex flex-col">
                <CallTranscript />
              </TabsContent>

              {hasSummary && (
                <TabsContent value="summary" className="flex-1 h-full min-h-0 flex flex-col">
                  <CallSummary />
                </TabsContent>
              )}

              {hasExtractedData && (
                <TabsContent value="extracted_data" className="flex-1 h-full min-h-0 flex flex-col">
                  <CallExtractedData />
                </TabsContent>
              )}

              {hasSentiment && (
                <TabsContent value="sentiment" className="flex-1 h-full min-h-0 flex flex-col">
                  <CallSentiment />
                </TabsContent>
              )}
            </Tabs>
          </div>
        </div>
      )}
    </>
  );
};

export const CallDetailPage = ({ toggle, callId }: Props) => {
  const query = useCallDetailQuery(callId, {
    enabled: !!callId,
  });

  const callDetail = query.data;

  return (
    <Sheet open={true} onOpenChange={toggle}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl h-full min-h-0 flex flex-col"
        onOpenAutoFocus={e => {
          e.preventDefault();
        }}
      >
        <CallDetailProvider data={callDetail}>
          {query.isLoading ? (
            <div className="flex-1 flex items-center justify-center">Loading</div>
          ) : (
            <Content />
          )}
        </CallDetailProvider>
      </SheetContent>
    </Sheet>
  );
};
